import {
  AlertTriangle, Check, Crosshair, ExternalLink, FileSpreadsheet, FileText, Mail, Scale,
  Search, Undo2,
} from 'lucide-react';
import type {
  DocRef,
  LocatedCandidate,
  Provenance,
  ReviewItem,
  SearchedDoc,
  SourceAnchor,
  SourceFileRef,
} from '@/services/ingestion';
import {
  DECISION_TEXT, OUTCOME_CLS, OUTCOME_LABEL, STATUS_META, anchorFor,
  displayValue, locatorText,
} from './datapointFormat';
import { useDatapointOptions } from './useDatapointOptions';

/**
 * One datapoint, in full — the evidence a person decides on.
 *
 * The Data Intelligence Review's card for a checklist row: where the value came
 * from and a way straight to it, every other statement of it in the package,
 * where the engine looked, what confirming it would contradict, and the
 * decision controls. It decides one datapoint at a time through
 * `onDecide(field, kind, value, provenance, reason)` and `onWithdraw(field)`,
 * and never more than one.
 */

function docIcon(doc?: DocRef | null) {
  if (!doc) return <FileText size={10} className="text-stone-500" />;
  if (doc.kind === 'derivation') return <Scale size={10} className="text-sky-400" />;
  if (doc.kind === 'email') return <Mail size={10} className="text-blue-400" />;
  if (doc.kind === 'xlsx' || doc.kind === 'excel' || doc.kind === 'csv') {
    return <FileSpreadsheet size={10} className="text-emerald-400" />;
  }
  return <FileText size={10} className="text-stone-400" />;
}

// ---------------------------------------------------------------------------
// Provenance — where a value came from, and a way straight to it
// ---------------------------------------------------------------------------

export function ProvenanceCard({
  provenance,
  onOpen,
  title = 'Where it came from',
}: {
  provenance?: Provenance | null;
  onOpen?: (sha: string, anchor: SourceAnchor | null) => void;
  title?: string;
}) {
  if (!provenance?.doc) {
    return (
      <div className="rounded border border-stone-800 bg-stone-900/40 px-3 py-2">
        <span className="block text-[9px] uppercase tracking-wide text-stone-600">{title}</span>
        <p className="mt-1 text-[11px] text-stone-500">
          Nothing in this package established a value, so there is no source to show.
        </p>
      </div>
    );
  }
  const sha = provenance.doc.sha256;
  const where = locatorText(provenance);
  const openable = Boolean(sha && onOpen);
  return (
    <div className="rounded border border-stone-800 bg-stone-900/40 px-3 py-2">
      <span className="block text-[9px] uppercase tracking-wide text-stone-600">{title}</span>
      <button
        type="button"
        disabled={!openable}
        onClick={openable ? () => onOpen!(sha!, anchorFor(provenance)) : undefined}
        title={openable
          ? 'Open the source and highlight this exact place'
          : provenance.doc.kind === 'derivation'
            ? 'The engine computed this — the inputs it used are listed below'
            : 'This value cannot be traced to a stored file on this record'}
        className={`mt-1 flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left text-[11px] transition ${
          openable
            ? 'text-stone-200 hover:bg-amber-400/10 hover:text-amber-200'
            : 'cursor-default text-stone-400'
        }`}
      >
        {docIcon(provenance.doc)}
        <span className="truncate font-medium">{provenance.doc.filename}</span>
        {where && <span className="shrink-0 text-stone-500">· {where}</span>}
        {openable && <ExternalLink size={9} className="ml-auto shrink-0 text-stone-600" />}
      </button>
      {provenance.snippet && (
        <div className="mt-1.5 rounded border border-stone-800 bg-black/40 px-2 py-1 font-mono text-[10px] text-stone-300">
          {provenance.label && <span className="text-stone-500">{provenance.label}: </span>}
          {provenance.snippet}
        </div>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[9px] text-stone-600">
        {provenance.methodLabel && <span>{provenance.methodLabel}</span>}
        {provenance.normalization && <span>· {provenance.normalization}</span>}
        {/* Only meaningful for a value read out of a DOCUMENT — a computed
            value has no file to have been matched to. */}
        {provenance.resolvedBy === 'filename' && provenance.doc.kind !== 'derivation' && (
          <span className="text-amber-400/70">· matched by filename, not content hash</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One datapoint, in full
// ---------------------------------------------------------------------------

export function DatapointPanel({
  item, actions, located, searchedIn, searching, busy, draft, onDraft, reasonDraft,
  onReasonDraft, sourceFiles, scopeSha, onScopeSha, onOpenSource, onDesignate,
  onLocate, onDecide, onWithdraw,
}: {
  item: ReviewItem;
  /** The verbs the server offers on this row. A verb it does not offer is not
   *  drawn: "not available" on a deal's name, a withdraw on a decision made
   *  elsewhere. Absent, every verb is drawn. */
  actions?: readonly string[];
  located: LocatedCandidate[];
  searchedIn?: SearchedDoc[];
  searching: boolean;
  busy: boolean;
  draft: string;
  onDraft: (v: string) => void;
  reasonDraft: string;
  onReasonDraft: (v: string) => void;
  sourceFiles: SourceFileRef[];
  scopeSha: string;
  onScopeSha: (v: string) => void;
  onOpenSource?: (sha: string, anchor: SourceAnchor | null) => void;
  onDesignate?: (field: string, label: string) => void;
  onLocate: (field: string, opts?: { sha256?: string }) => void;
  onDecide: (
    field: string,
    kind: 'confirm' | 'correct' | 'accept_located' | 'unavailable',
    value?: unknown,
    provenance?: Provenance | null,
    reason?: string,
  ) => void;
  onWithdraw: (field: string) => void;
}) {
  const meta = STATUS_META[item.status] ?? STATUS_META.missing;
  const optionsFor = useDatapointOptions();
  const options = optionsFor(item.field);
  const rivals = (item.candidates ?? []).filter((c) => c.value != null && c.value !== '');
  const searched = searchedIn ?? item.searched ?? [];
  const offers = (verb: string) => actions == null || actions.includes(verb);

  return (
    <div className="space-y-3">
      {/* Value + status */}
      <div>
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-stone-100">{item.label}</span>
          <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[8px] font-medium uppercase tracking-wide ${meta.cls}`}>
            {meta.label}
          </span>
        </div>
        <div className="mt-0.5 font-mono text-[15px] text-stone-100">
          {displayValue(item.value)}
        </div>
        {/* Why it needed a person, and the engine's proposal — both describe
            the state BEFORE anyone decided. Once a decision is recorded they
            are history, not a caution about the value now on the record. */}
        {!item.decision && (item.reasons?.length ?? 0) > 0 && (
          <ul className="mt-1.5 space-y-0.5">
            {item.reasons!.map((r, i) => (
              <li key={i} className="flex items-start gap-1.5 text-[10.5px] text-amber-300/85">
                <AlertTriangle size={9} className="mt-0.5 shrink-0" /> {r.text}
              </li>
            ))}
          </ul>
        )}
        {!item.decision && item.trace && (
          <div className="mt-1.5 rounded border border-sky-400/20 bg-sky-400/[0.06] px-2 py-1 font-mono text-[10px] text-sky-200">
            {item.trace}
          </div>
        )}
      </div>

      {/* Where it came from */}
      <ProvenanceCard provenance={item.provenance} onOpen={onOpenSource} />

      {/* The derivation's own inputs, each traceable */}
      {!item.decision && (item.inputs?.length ?? 0) > 0 && (
        <Section title="Computed from">
          {item.inputs!.map((input, i) => (
            <div key={i} className="flex items-center gap-2 py-0.5 text-[10.5px]">
              <span className="w-[150px] shrink-0 text-stone-500">{input.label ?? input.field}</span>
              <span className="font-mono text-stone-200">{displayValue(input.value)}</span>
              {input.provenance?.doc?.sha256 && onOpenSource && (
                <button
                  type="button"
                  onClick={() => onOpenSource(input.provenance!.doc.sha256!, anchorFor(input.provenance))}
                  className="text-stone-600 underline decoration-dotted underline-offset-2 transition hover:text-amber-300"
                >
                  {input.provenance.doc.filename}
                </button>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* Everything else the package says */}
      {(rivals.length > 0 || located.length > 0) && (
        <Section title="Everything else the package says">
          <div className="space-y-1.5">
            {rivals.map((c, i) => (
              <CandidateRow
                key={`r-${i}`}
                value={c.value}
                why={c.why}
                relation={c.relation}
                provenance={c.provenance}
                basis={c.basis}
                onOpenSource={onOpenSource}
                onUse={() => onDecide(item.field, 'correct', c.value, c.provenance)}
                disabled={busy}
              />
            ))}
            {located.map((c, i) => (
              <CandidateRow
                key={`l-${i}`}
                value={c.value}
                why={c.why.join(' · ')}
                relation="located"
                provenance={c.provenance}
                onOpenSource={onOpenSource}
                onUse={c.value == null ? undefined
                  : () => onDecide(item.field, 'accept_located', c.value, c.provenance)}
                disabled={busy}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Readings the engine did NOT offer as rivals: a ratio beside dollar amounts
          is not a second figure for the same line. Kept, with the cell, so nothing
          the package said is hidden. */}
      {(item.setAside?.length ?? 0) > 0 && (
        <Section title="Set aside — cannot be the same quantity">
          <div className="space-y-1.5">
            {item.setAside!.map((r, i) => (
              <CandidateRow
                key={`s-${i}`}
                value={r.value}
                why={r.reason}
                relation="set aside"
                provenance={r.provenance ?? undefined}
                onOpenSource={onOpenSource}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Where we looked, and how to point the engine somewhere else */}
      <Section title="Where we looked">
        {searched.length > 0 ? (
          <div className="space-y-0.5">
            {searched.map((s, i) => (
              <div key={i} className="flex items-center gap-1.5 py-0.5 text-[10.5px]">
                {docIcon(s.doc)}
                <span className="min-w-0 flex-1 truncate text-stone-300">{s.doc.filename}</span>
                <span className={`shrink-0 ${OUTCOME_CLS[s.outcome]}`}>
                  {OUTCOME_LABEL[s.outcome]}
                </span>
                {s.doc.sha256 && (
                  <button
                    type="button"
                    onClick={() => onLocate(item.field, { sha256: s.doc.sha256! })}
                    disabled={searching}
                    title={s.detail}
                    className="shrink-0 rounded border border-stone-700 bg-stone-800/60 px-1.5 py-0.5 text-[8px] uppercase tracking-wide text-stone-400 transition hover:border-amber-400/50 hover:text-amber-300 disabled:opacity-40"
                  >
                    look again
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[10.5px] text-stone-500">
            No search has run for this datapoint yet.
          </p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-stone-800 pt-2">
          <span className="text-[10px] text-stone-500">Look for it in</span>
          <select
            value={scopeSha}
            onChange={(e) => onScopeSha(e.target.value)}
            className="input w-[190px] text-[10.5px]"
          >
            <option value="">every document</option>
            {sourceFiles.map((f) => (
              <option key={f.sha256} value={f.sha256}>
                {f.kind === 'email_source' ? 'the email' : f.filename}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => onLocate(item.field, scopeSha ? { sha256: scopeSha } : {})}
            disabled={searching}
            className="inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-medium text-amber-300 transition hover:border-amber-400 hover:bg-amber-400/20 disabled:opacity-40"
          >
            <Search size={10} /> {searching ? 'Searching…' : 'Find it'}
          </button>
          {onDesignate && (
            <button
              type="button"
              onClick={() => onDesignate(item.field, item.label)}
              className="inline-flex items-center gap-1 rounded border border-stone-700 bg-stone-800/60 px-2 py-1 text-[10px] text-stone-300 transition hover:border-amber-400/50 hover:text-amber-300"
              title="Open the source and click the exact cell or line that holds it"
            >
              <Crosshair size={10} /> Point at it
            </button>
          )}
        </div>
      </Section>

      {/* Consistency */}
      {(item.consistency?.length ?? 0) > 0 && (
        <Section title="What this agrees with">
          {item.consistency!.map((c, i) => (
            <div key={i} className="flex items-start gap-1.5 py-0.5 text-[10.5px]">
              <Scale
                size={10}
                className={`mt-0.5 shrink-0 ${c.verdict === 'agrees' ? 'text-emerald-400' : 'text-amber-400'}`}
              />
              <span className={c.verdict === 'agrees' ? 'text-stone-400' : 'text-amber-200'}>
                <span className="text-stone-500">{c.check}:</span> {c.detail}
              </span>
            </div>
          ))}
        </Section>
      )}

      {/* Nearest evidence (unparsed lines, guidance terms) */}
      {(item.evidence?.length ?? 0) > 0 && (
        <Section title="Nearest evidence">
          {item.evidence!.map((e, i) => (
            <div key={i} className="rounded border border-stone-800 bg-black/30 px-2 py-1 text-[10px] text-stone-400">
              <span className="text-stone-500">
                {e.kind === 'guidance' ? 'guidance doc' : 'unparsed line'} · {e.source ?? ''}
                {e.lineNo ? ` · line ${e.lineNo}` : ''}:
              </span>{' '}
              {e.text ?? displayValue(e.value)}
            </div>
          ))}
        </Section>
      )}

      {/* Decision */}
      <div className="sticky bottom-0 -mx-4 border-t border-stone-800 bg-stone-950/95 px-4 pb-1 pt-2.5 backdrop-blur">
        {item.decision ? (
          <div className="flex items-center gap-2">
            <Check size={12} className="text-emerald-400" />
            <span className="text-[11px] text-emerald-300">
              {DECISION_TEXT[item.decision.kind]} by {item.decision.by}
              {item.decision.at ? ` · ${new Date(item.decision.at).toLocaleString()}` : ''}
              {item.decision.reason ? ` — ${item.decision.reason}` : ''}
            </span>
            {offers('withdraw') && (
              <button
                type="button"
                onClick={() => onWithdraw(item.field)}
                disabled={busy}
                className="ml-auto inline-flex items-center gap-1 rounded border border-stone-700 px-2 py-1 text-[10px] text-stone-400 transition hover:border-amber-400/50 hover:text-amber-300 disabled:opacity-40"
              >
                <Undo2 size={10} /> Withdraw
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                disabled={busy || item.value == null}
                onClick={() => onDecide(item.field, 'confirm', undefined, item.provenance)}
                title={item.value == null
                  ? 'There is no extracted value to confirm'
                  : 'Accept the value as extracted'}
                className="btn-primary px-3 py-1.5 text-[11px] disabled:opacity-40"
              >
                {busy ? 'Saving…' : 'Confirm as read'}
              </button>
              {options ? (
                <select
                  value={draft}
                  onChange={(e) => onDraft(e.target.value)}
                  className="input w-[200px] text-[11px]"
                >
                  <option value="">— choose a value —</option>
                  {options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  value={draft}
                  onChange={(e) => onDraft(e.target.value)}
                  placeholder="Enter the correct value"
                  className="input w-[220px] text-[11px]"
                />
              )}
              <button
                type="button"
                disabled={busy || !draft.trim()}
                onClick={() => onDecide(item.field, 'correct', draft.trim(), null)}
                className="btn-secondary px-3 py-1.5 text-[11px] disabled:opacity-40"
              >
                Use this
              </button>
              {offers('unavailable') && (
                <div className="ml-auto flex items-center gap-1.5">
                  <input
                    value={reasonDraft}
                    onChange={(e) => onReasonDraft(e.target.value)}
                    placeholder="Why is it unavailable?"
                    className="input w-[190px] text-[10.5px]"
                  />
                  <button
                    type="button"
                    disabled={busy || !reasonDraft.trim()}
                    onClick={() => onDecide(item.field, 'unavailable', undefined, null, reasonDraft.trim())}
                    className="rounded border border-stone-700 px-2 py-1.5 text-[10.5px] text-stone-400 transition hover:border-stone-500 hover:text-stone-200 disabled:opacity-40"
                  >
                    Not available
                  </button>
                </div>
              )}
            </div>
            <p className="mt-1 text-[9px] text-stone-600">
              Each decision applies on its own and is audited with the evidence it rests on.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded border border-stone-800 bg-stone-900/30 px-3 py-2">
      <span className="mb-1 block text-[9px] uppercase tracking-wide text-stone-600">{title}</span>
      {children}
    </div>
  );
}

export function CandidateRow({
  value, why, relation, provenance, onOpenSource, onUse, disabled, basis,
}: {
  value: unknown;
  why?: string;
  /** What kind of reading this is — the period and the tab, for a workbook figure. */
  basis?: string | null;
  relation?: string;
  provenance?: Provenance | null;
  onOpenSource?: (sha: string, anchor: SourceAnchor | null) => void;
  onUse?: () => void;
  disabled?: boolean;
}) {
  const sha = provenance?.doc?.sha256;
  const where = locatorText(provenance);
  const tone = relation === 'corroborates' ? 'text-emerald-300/80'
    : relation === 'conflicts' ? 'text-red-300/80'
    : relation === 'located' ? 'text-amber-300/90'
    : 'text-stone-400';
  return (
    <div className="rounded border border-stone-800 bg-black/25 px-2 py-1.5">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] text-stone-100">{displayValue(value)}</span>
        {relation && (
          <span className={`text-[8px] uppercase tracking-wide ${tone}`}>{relation}</span>
        )}
        {onUse && (
          <button
            type="button"
            onClick={onUse}
            disabled={disabled}
            className="ml-auto rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[9px] uppercase tracking-wide text-amber-300 transition hover:border-amber-400 hover:bg-amber-400/20 disabled:opacity-40"
          >
            use this
          </button>
        )}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[9.5px] text-stone-500">
        {provenance?.doc && (
          sha && onOpenSource ? (
            <button
              type="button"
              onClick={() => onOpenSource(sha, anchorFor(provenance))}
              className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-2 transition hover:text-amber-300"
            >
              {docIcon(provenance.doc)} {provenance.doc.filename}
              {where ? ` · ${where}` : ''}
            </button>
          ) : (
            <span className="inline-flex items-center gap-1">
              {docIcon(provenance.doc)} {provenance.doc.filename}
              {where ? ` · ${where}` : ''}
            </span>
          )
        )}
        {why && <span className="text-stone-600">— {why}</span>}
      </div>
      {basis && (
        <div className="mt-0.5 text-[9.5px] text-sky-300/80" data-testid="candidate-basis">{basis}</div>
      )}
    </div>
  );
}
